import React, { useEffect, useId, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { useSaveToast } from '../ui/SaveToast';
import { pluralWord } from '../events/eventWords';
import {
    BOOKING_WORD_PATTERN, BOOKING_WORD_PRESETS, BOOKING_WORD_RULE, DEFAULT_BOOKING_WORD,
    loadBookingWord, saveBookingWord, singularFromPlural,
} from '../../lib/companyWords';

// Admin Center > Company Words. One question: "What do you call a booking?" The answer renames the staff app's
// Engagements area (menu, page title, buttons, empty states) and the client app's Bookings tile and page.
const CUSTOM = '__custom__';
const OPTION = 'flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm';

export default function CompanyWordsSettings({ orgId, client = supabaseClient, onSaved }) {
    const toast = useSaveToast();
    const groupId = useId();
    const [saved, setSaved] = useState(DEFAULT_BOOKING_WORD);
    const [clientWord, setClientWord] = useState('');
    const [choice, setChoice] = useState(DEFAULT_BOOKING_WORD);
    const [custom, setCustom] = useState('');
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        if (!orgId) { setBusy(false); return () => { active = false; }; }
        (async () => {
            const word = await loadBookingWord(client, orgId);
            let label = '';
            try {
                const { data } = await client.from('org_settings').select('bookings_label').eq('org_id', orgId).maybeSingle();
                label = data?.bookings_label || '';
            } catch (failure) { /* the client word is shown only as a note */ }
            if (!active) return;
            const current = word || DEFAULT_BOOKING_WORD;
            setSaved(current);
            setClientWord(label);
            if (BOOKING_WORD_PRESETS.includes(current)) setChoice(current);
            else { setChoice(CUSTOM); setCustom(current); }
            setBusy(false);
        })();
        return () => { active = false; };
    }, [client, orgId]);

    const word = choice === CUSTOM ? singularFromPlural(custom) : choice;
    const valid = BOOKING_WORD_PATTERN.test(String(word || '').trim());

    async function save(event) {
        event.preventDefault();
        setError('');
        if (!valid) { setError(BOOKING_WORD_RULE); return; }
        setBusy(true);
        const result = await saveBookingWord(client, orgId, word.trim());
        setBusy(false);
        if (!result.ok) {
            const message = result.step === 'client'
                ? `The staff app now says "${pluralWord(word.trim())}", but the client app word could not be saved: ${result.message}`
                : `The word could not be saved: ${result.message}`;
            if (result.step === 'client') setSaved(word.trim());
            setError(message);
            toast.failed(message);
            return;
        }
        setSaved(result.booking);
        setClientWord(result.plural);
        toast.saved();
        onSaved?.(result.booking);
    }

    const preview = valid ? word.trim() : saved;
    return (
        <section className="space-y-4 text-[var(--ink)]" aria-labelledby={`${groupId}-title`} data-testid="company-words">
            <div>
                <h2 id={`${groupId}-title`} className="text-lg font-semibold">What do you call a booking?</h2>
                <p className="mt-1 text-sm text-[var(--ink-muted)]">
                    Pick the word your team and your clients use. It changes the staff menu, page titles, buttons and empty lists,
                    and the client app&apos;s tile and page. Your records do not change. Only an Owner or Admin can change it.
                </p>
            </div>
            <form onSubmit={save} className="space-y-4">
                <fieldset disabled={busy} className="space-y-2">
                    <legend className="sr-only">What do you call a booking?</legend>
                    {BOOKING_WORD_PRESETS.map((preset) => (
                        <label key={preset} className={`${OPTION} ${choice === preset ? 'border-[var(--selected-pill-border)]' : 'border-[var(--surface-border)]'}`}>
                            <input type="radio" name={`${groupId}-word`} value={preset} checked={choice === preset}
                                onChange={() => { setChoice(preset); setError(''); }} />
                            <span className="font-medium">{pluralWord(preset)}</span>
                            <span className="text-[var(--ink-muted)]">“New {preset}”</span>
                        </label>
                    ))}
                    <label className={`${OPTION} flex-wrap ${choice === CUSTOM ? 'border-[var(--selected-pill-border)]' : 'border-[var(--surface-border)]'}`}>
                        <input type="radio" name={`${groupId}-word`} value={CUSTOM} checked={choice === CUSTOM}
                            onChange={() => { setChoice(CUSTOM); setError(''); }} />
                        <span className="font-medium">My own word</span>
                        {choice === CUSTOM && (
                            <input type="text" aria-label="Your word for one booking" maxLength={24} value={custom}
                                placeholder="One booking, e.g. Ride" onChange={(event) => { setCustom(event.target.value); setError(''); }}
                                className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-[var(--surface-border)] px-3 py-2" />
                        )}
                    </label>
                </fieldset>
                <p className="text-sm" data-testid="company-words-preview">
                    Your team will see <strong>{pluralWord(preview)}</strong> in the menu and <strong>+ New {preview}</strong> on the button.
                    Clients will see <strong>{pluralWord(preview)}</strong> in their app.
                </p>
                {clientWord && clientWord !== pluralWord(saved) && (
                    <p className="text-sm text-[var(--ink-muted)]" data-testid="company-words-client-note">
                        Today your client app says “{clientWord}” and your team sees “{pluralWord(saved)}”. Saving sets both to the same word.
                    </p>
                )}
                {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
                <button type="submit" disabled={busy || !valid}
                    className="ia-hover-pulse min-h-[44px] rounded-full border border-[var(--selected-pill-border)] px-5 py-2 font-medium text-[var(--selected-pill-ink)] disabled:opacity-50">
                    {busy ? 'Saving…' : 'Save word'}
                </button>
            </form>
        </section>
    );
}
